import type { JSX } from 'react'

import { useTranslation } from '../i18n.js'
import { useWriteQueue } from './use-write-queue.js'

/**
 * What the queue is doing, on every screen. `DESIGN-TOKENS.md` §16.5; `OFF-001a`, `OFF-005`.
 *
 * > "**offline indicator with pending count, always visible when not clean**"
 *
 * **The counter had the count and nowhere to show it.** `statusLine` tells a screen what happened
 * to the write that screen made, which is the right thing beside a form and the wrong thing for
 * this question: a dispenser who queued a payment on the Cash drawer screen and walked to the work
 * queue had no way to know it was still unsent, and a blocked write stops every write behind it.
 *
 * **Absent when the queue is clean**, which §16.5 says and which matters more than it reads. An
 * indicator that is always there is furniture: it is read once in the first week and never again,
 * so the one morning it says something is the morning nobody looks. This appears when there is
 * something to say and is not there otherwise.
 *
 * **Three states and they are not the same urgency.** Offline with writes waiting is normal in a
 * Pakistani shop and says so calmly. Writes waiting while online is a slow connection. A blocked
 * write is the one a person has to act on, and `BlockedWrites` above the screen is where they act:
 * this says it is there and that one says what it was.
 */

export function QueueFlag(): JSX.Element | null {
  const { t } = useTranslation()
  const { snapshot } = useWriteQueue()
  const waiting = snapshot.pending + snapshot.sending

  if (snapshot.blocked > 0) {
    return (
      <span className="lm-queue-flag lm-queue-flag--blocked" role="status">
        {t('queueFlag.blocked', { count: snapshot.blocked })}
      </span>
    )
  }

  if (!snapshot.online) {
    // The count is said even at zero here, because "offline" on its own leaves a shopkeeper
    // wondering whether what they just did is safe. Nothing waiting is the reassuring answer.
    return (
      <span className="lm-queue-flag lm-queue-flag--offline" role="status">
        {waiting === 0 ? t('queueFlag.offlineClean') : t('queueFlag.offline', { count: waiting })}
      </span>
    )
  }

  if (waiting > 0) {
    return (
      <span className="lm-queue-flag" role="status">
        {t('queueFlag.waiting', { count: waiting })}
      </span>
    )
  }

  return null
}
